import React, { useContext, useState, useEffect, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from "../../services/firebase";
import { POST } from '../../services/HTTPHandler';
import { CurrentUserContext } from '../../contexts/CurrentUserContext';
import {
    Navbar,
    NavbarBrand,
    NavbarContent,
    NavbarItem,
    NavbarMenu,
    NavbarMenuItem,
    NavbarMenuToggle,
    Link,
    Avatar,
    Button,
    Dropdown,
    DropdownMenu,
    DropdownItem,
    DropdownTrigger,
} from "@nextui-org/react";

import '../../styles/landingNavbar.css'
import UpgradeButton from '../Form/UpgradeButton';
import Notifications from './Notifications';
import classNames from 'classnames';

const menuItems = [
    {label: "How it works", href: "#howItWorks"},
    {label: "Features", href: "#features"},
    {label: "Pricing", href: "#pricing"},
    {label: "Contact", href: "#contact"},
];

const SECTION_IDS = ['howItWorks', 'features', 'pricing', 'contact'];

const LandingNavbar = props => {
    const { 
        userStatus,
        showUpgrade,
        notifications,
        navigateToHome,
        showNotification,
    } = props;
    const user = useContext(CurrentUserContext);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSignedIn, setIsSignedIn] = useState(!!user.uid);
    const [activeMenuItem, setActiveMenuItem] = useState(-1);

    const logoutHandler = () => {
        signOut(auth);
        POST('/activity/logout');
        setIsSignedIn(false);
    };

    const ratiosRef = useRef({});

    useEffect(() => {
        const sectionEls = SECTION_IDS
            .map(id => document.getElementById(id))
            .filter(Boolean);
        if (sectionEls.length === 0) return;

        const updateActive = () => {
            let maxRatio = 0;
            let maxIndex = -1;
            for (let i = 0; i < SECTION_IDS.length; i++) {
                const r = ratiosRef.current[i] ?? 0;
                if (r > maxRatio || (r === maxRatio && r > 0 && i > maxIndex)) {
                    maxRatio = r;
                    maxIndex = i;
                }
            }
            setActiveMenuItem(maxIndex);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const index = SECTION_IDS.indexOf(entry.target.id);
                    if (index >= 0) ratiosRef.current[index] = entry.intersectionRatio;
                });
                updateActive();
            },
            { root: null, rootMargin: '-80px 0px 0px 0px', threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
        );

        sectionEls.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const navLinkBaseClass = "!font-semibold px-2 py-1 rounded-lg transition-colors duration-150 hover:text-primary";
    const loginLinkClass = "transition-colors duration-150 hover:text-primary";

    return (
        <Navbar
            maxWidth="full"
            className={classNames('landing-navbar-root', {
                'bg-base-background': showNotification,
            })}
            classNames={{
                toggle: "transition-opacity duration-150 data-[hover=true]:opacity-80",
            }}
            onMenuOpenChange={setIsMenuOpen}
        >
            <NavbarBrand className="logo">
                <NavbarMenuToggle
                    aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                    className="sm:hidden"
                />
                <Link href={navigateToHome ? '/' : '#home'} aria-label="go to home" onClick={() => setActiveMenuItem(-1)} className="transition-opacity duration-150 hover:opacity-90">
                    <img src="https://raw.githubusercontent.com/arvindrk/my-retro/master/Logo.webp" alt="Logo" />
                </Link>
            </NavbarBrand>

            {!showNotification  && <NavbarContent className="hidden sm:flex gap-4" justify="center">
                {menuItems.map((item, index) => {
                    const isActive = activeMenuItem === index;
                    return <NavbarItem key={`${item}-${index}`} isActive={isActive}>
                        <Link
                            href={item.href}
                            onClick={() => setActiveMenuItem(index)}
                            className={classNames(navLinkBaseClass, isActive && "text-primary bg-primary/10")}
                        >
                            {item.label}
                        </Link>
                    </NavbarItem>
                })}
            </NavbarContent>}
            
            <NavbarContent as="div" justify="end">
                <NavbarItem>
                    {showUpgrade && <UpgradeButton userStatus={userStatus} />}
                </NavbarItem>
                <NavbarItem>
                    {showNotification && <Notifications
                        notifications={notifications}
                        count={Object.keys(notifications).length}
                    />}
                </NavbarItem>
                {user.uid && isSignedIn ? <Dropdown placement="bottom-end">
                    <DropdownTrigger>
                        <Avatar
                            size="sm"
                            isBordered
                            as="button"
                            color="secondary"
                            src={user.photoURL}
                            name={user.displayName.split('.')[0]}
                            className="transition-transform duration-150 hover:scale-105 cursor-pointer"
                        />
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Profile Actions" variant="flat">
                        <DropdownItem key="profile" className="h-14 gap-2">
                            <p className="font-semibold">Signed in as</p>
                            <p className="font-semibold">{user.email}</p>
                        </DropdownItem>
                        <DropdownItem key="dashboard" href="/dashboard">My Dashboard</DropdownItem>
                        <DropdownItem key="settings-profile" href="/settings/user">My Profile</DropdownItem>
                        <DropdownItem key="settings-account" href="/settings/subscription">Manage Subscription</DropdownItem>
                        <DropdownItem key="help_and_feedback">Help & Feedback</DropdownItem>
                        <DropdownItem key="logout" color="danger" onClick={logoutHandler}>
                            Log Out
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown> : <>
                        <NavbarItem className="md:flex font-semibold">
                            <Link href="/login" className={loginLinkClass}>Login</Link>
                        </NavbarItem>
                        <NavbarItem className="md:flex">
                            <Button as={Link} color="primary" href="/signup" variant="flat" className="transition-opacity duration-150 hover:opacity-90 font-semibold">
                                Try for Free
                            </Button>
                        </NavbarItem>
                </>}
            </NavbarContent>
            <NavbarMenu>
                {menuItems.map((item, index) => (
                    <NavbarMenuItem key={`${item}-${index}`}>
                        <Link
                            color={
                                index === activeMenuItem ? "primary" : "foreground"
                            }
                            className="w-full py-2 px-3 rounded-lg transition-colors duration-150 hover:bg-default-100"
                            href={item.href}
                            size="sm"
                            onClick={() => {setActiveMenuItem(index); setIsMenuOpen(false)}}
                        >
                            {item.label}
                        </Link>
                    </NavbarMenuItem>
                ))}
            </NavbarMenu>
        </Navbar>
    );
};

export default LandingNavbar;